import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Check } from '@phosphor-icons/react';
import './FieldDropdown.scss';

export interface DropdownOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  shortcut?: string;
}

interface FieldDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  allowClear?: boolean;
}

const FieldDropdown: React.FC<FieldDropdownProps> = ({
  options,
  value,
  onChange,
  onClose,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No options',
  allowClear = true
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [alignRight, setAlignRight] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      if (rect.right > window.innerWidth) {
        setAlignRight(true);
      }
    }
  }, []);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Small delay to prevent immediate closing when the button is clicked
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setSearchQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => 
        prev < filteredOptions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
        handleSelect(filteredOptions[focusedIndex].value);
      }
    }
  };

  return (
    <div className={`tasksFieldDropdown ${alignRight ? 'tasksFieldDropdownAlignRight' : ''}`} ref={dropdownRef}>
      <div className="tasksFieldDropdownPortal">
        <div className="tasksFieldDropdownSearch">
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setFocusedIndex(-1);
            }}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            className="tasksFieldDropdownSearchInput"
          />
        </div>

        <div className="tasksFieldDropdownOptions">
          {allowClear && (
            <button
              type="button"
              className={`tasksFieldDropdownOption ${focusedIndex === -1 ? 'tasksFieldDropdownOptionFocused' : ''}`}
              onClick={() => handleSelect('')}
              onMouseEnter={() => setFocusedIndex(-1)}
            >
              <span className="tasksFieldDropdownOptionIcon">—</span>
              <span className="tasksFieldDropdownOptionLabel">No {placeholder.toLowerCase()}</span>
              {!value && (
                <Check className="tasksFieldDropdownOptionCheck" size={14} weight="bold" />
              )}
            </button>
          )}

          {filteredOptions.map((option, index) => (
              <button
                key={option.value}
                type="button"
                className={`tasksFieldDropdownOption ${index === focusedIndex ? 'tasksFieldDropdownOptionFocused' : ''} ${option.value === value ? 'tasksFieldDropdownOptionSelected' : ''}`}
                onClick={() => handleSelect(option.value)}
                onMouseEnter={() => setFocusedIndex(index)}
              >
                {option.icon && (
                  <div className="tasksFieldDropdownOptionIcon">{option.icon}</div>
                )}
                <span className="tasksFieldDropdownOptionLabel">{option.label}</span>
                {option.value === value && (
                  <Check className="tasksFieldDropdownOptionCheck" size={14} weight="bold" />
                )}
                {option.shortcut && (
                  <span className="tasksFieldDropdownOptionShortcut">{option.shortcut}</span>
                )}
              </button>
          ))}

          {filteredOptions.length === 0 && (
            <div className="tasksFieldDropdownEmpty">{emptyMessage}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FieldDropdown;


